/**
 * Pritect Beacon, consent script entry point.
 *
 * Customers never hand-write this tag. The dashboard's Deploy tab generates it
 * from the published release manifest. The shape it emits:
 *
 * <script
 *   src="https://ynbeopknlxoayzdocsmu.supabase.co/storage/v1/object/public/consent-scripts/v1/pritect-beacon.min.js"
 *   data-domain-id="YOUR_DOMAIN_ID"
 *   data-api-url="https://your-project.supabase.co"
 *   data-anon-key="YOUR_SUPABASE_ANON_KEY"
 * ></script>
 *
 * That stable URL serves the LOADER (src/loader/loader-entry.ts), not this
 * bundle. The loader starts blocking scripts synchronously and then injects
 * this file pinned by content hash, which is how a release reaches live sites
 * with no snippet to re-copy. This bundle also runs standalone, with no loader,
 * for version-pinned embeds; see the window.__pbLoader handoff in boot() below.
 *
 * The bucket has two channels. releases/pritect-beacon.<hash>.min.js is
 * immutable and MUST carry an integrity attribute. v1/pritect-beacon.min.js is
 * rewritten by every publish and MUST NOT: the next deploy would break the hash
 * and the browser would silently refuse to run the script, which is exactly
 * what took consent management off pritect.ai on 9 Aug 2026. See
 * scripts/check-sri-pinning.mjs.
 */

import { ConsentEngine } from "./core/ConsentEngine";
import { GoogleConsentMode } from "./gcm/GoogleConsentMode";
import { getScriptConfig, installTcfStub } from "./scriptConfig";

const SUPABASE_URL = "https://ynbeopknlxoayzdocsmu.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_vU9Z7i0RWBnGJtu3p7_t5Q_JgZuhJBP";
let engine: ConsentEngine | null = null;
let pendingPreferencesOpen = false;

async function boot(): Promise<void> {
  // When the stable loader injected this bundle it has already parsed the embed
  // config, installed the GCM defaults and the TCF stub, and been intercepting
  // scripts since before any other tag on the page ran. Redoing any of that
  // here would overwrite live state, so its work is adopted instead. Absent for
  // version-pinned embeds, which load this bundle directly.
  const handoff = window.__pbLoader;

  const config = handoff?.config ?? getScriptConfig();
  if (!config) return;

  // Synchronous consent-signalling setup, before any await, so it lands before
  // gtag / TCF vendor tags execute. Opt-in via data-gcm / data-tcf on the embed.
  if (config.gcm && !handoff?.gcmInstalled) {
    try { new GoogleConsentMode(true).setDefaults(); } catch { /* no-op */ }
  }
  if (config.tcf && !handoff?.tcfInstalled) {
    try { installTcfStub(); } catch { /* no-op */ }
  }

  engine = new ConsentEngine({
    domainId: config.domainId,
    apiUrl: (config.apiUrl ?? SUPABASE_URL).replace(/\/$/, ""),
    anonKey: config.anonKey ?? SUPABASE_ANON_KEY,
    lang: config.lang,
    blocker: handoff?.blocker,
  });

  await engine.init();
  if (pendingPreferencesOpen || handoff?.pendingPreferences) {
    pendingPreferencesOpen = false;
    if (handoff) handoff.pendingPreferences = false;
    engine.showPreferences();
  }
}

// Expose public API on window for custom integrations
declare global {
  interface Window {
    PritectBeacon: {
      showPreferences: () => void;
      revokeConsent: () => void;
    };
  }
}

// Placeholder public API — full implementation wired through ConsentEngine
window.PritectBeacon = {
  showPreferences: () => {
    if (engine) {
      engine.showPreferences();
      return;
    }
    pendingPreferencesOpen = true;
  },
  revokeConsent: () => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("pb_")) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    // Also clear the pb_consent cookie
    document.cookie = "pb_consent=; max-age=0; path=/; SameSite=Lax";
    window.location.reload();
  },
};

// Boot immediately so script interception starts before later tags execute.
void boot().catch((error) => {
  console.warn("[Pritect Beacon] Failed to initialize.", error);
  window.dispatchEvent(new CustomEvent("pb:error", { detail: { message: "Failed to initialize consent manager." } }));
});
